import { useCallback, useState } from 'react'

import { FormattedMessage } from 'react-intl'

import type { UserBank } from '@subaio/api-types/psd2'
import { useAsyncResource, useRefreshable } from '@subaio/components'
import { handleLogin, Psd2Error } from '@subaio/psd2login-web'
import { getCountry } from '@subaio/utils'

import type { BrandFeatures } from 'apiTypes/BrandFeatures'
import type { User } from 'apiTypes/User'

import { getDataToReview } from 'lib/getDataToReview'

import { ButtonGroup, ButtonLink, ButtonSolid } from 'components/Buttons'
import { InputError } from 'components/FormFields/Shared'
import { IconPlus, IconWarning } from 'components/Icons'
import { InfoBox } from 'components/InfoBox'
import { Card } from 'components/Layout'
import { List } from 'components/Lists'

import type { Step, StepType } from '../../Step'
import type { StepHandlers } from '../../stepHandler'
import type { UserMetadata } from '../../UserMetadata'
import { DefaultErrorSteps } from '../DefaultErrorSteps'
import { ItemBank } from './ItemBank'
import { Psd2loginError } from './Psd2loginErorr'
import { Title } from './Title'

type Props = TaggedType<Step, StepType.Accounts> & {
	userMetadata: UserMetadata
	brandFeatures: Array<BrandFeatures>
	user: User

	getLoginUrl: () => Promise<string>
	getBanks: () => Promise<Array<UserBank>>

	onNext: StepHandlers['onAccountSelectComplete']
}

export const Accounts = ({
	isBankDataReceived,
	user,
	userMetadata,
	brandFeatures,
	getLoginUrl,
	getBanks,
	onNext,
}: Props) => {
	const [psd2Status, setPsd2Status] = useState<'ready' | 'success' | Psd2Error>('ready')
	const { otherApplicantName, applicantType } = userMetadata
	const [showInputError, setShowInputError] = useState(false)

	const dataToReview = getDataToReview(brandFeatures)

	const { refreshable, refresh } = useRefreshable()

	const urlResource = useAsyncResource(
		useCallback(async () => {
			refreshable()

			return getLoginUrl()
		}, [getLoginUrl, refreshable]),
	)

	const selectedBanksResource = useAsyncResource(
		useCallback(async () => {
			if (urlResource.status !== 'done') return undefined
			refreshable()
			return getBanks()
		}, [getBanks, refreshable, urlResource]),
	)

	const onPsd2Login = useCallback(async () => {
		try {
			setShowInputError(false)
			await handleLogin({ url: urlResource.value! })
			setPsd2Status('success')
			refresh()
		} catch (error) {
			refresh()
			if (error instanceof Psd2Error) {
				console.error(error)
				return setPsd2Status(error)
			}
			throw error
		}
	}, [urlResource.value, refresh])

	const isSwedenFlow = getCountry(user.brandId) === 'SE'

	const arePsd2BanksSubmitted =
		psd2Status === 'ready' &&
		selectedBanksResource.value &&
		selectedBanksResource.value.length > 0 &&
		urlResource.status !== 'errored' &&
		isBankDataReceived !== true

	return (
		<Card>
			<p>
				{isSwedenFlow ? (
					<FormattedMessage
						id='flow.accounts.description1.sweden'
						defaultMessage='Before we can process your application, we need to verify your income.'
						values={{ dataToReview, applicantType, otherApplicantName }}
					/>
				) : (
					<FormattedMessage
						id='flow.accounts.description1'
						defaultMessage='{applicantType, select,
							SingleApplicant    { To get a complete overview of your {dataToReview, select, expenses {spending} income {income} both {spending and income} other {budget}}, add all your banks and accounts - including any credit cards that are not linked to your bank. }
							MultipleApplicants { To get a complete overview of your {dataToReview, select, expenses {expenses} income {income} both {expenses and income} other {budget}}, add all your banks and accounts, including joint accounts you have with {otherApplicantName}. Also include any credit cards that are not linked to your bank. }
							other {}
						}'
						values={{ dataToReview, applicantType, otherApplicantName }}
					/>
				)}
			</p>
			<p>
				{isSwedenFlow ? (
					<FormattedMessage
						id='flow.accounts.description2.sweden'
						defaultMessage='Click “Add Bank” and make sure to include all relevant accounts. If you have accounts that are not part of your personal finances—such as association or business accounts—you can exclude them.'
					/>
				) : (
					<FormattedMessage
						id='flow.accounts.description2'
						defaultMessage='Click "Add bank" and make sure all relevant accounts are included. If you have accounts that are not part of your personal finances, such as association or company accounts, you can deselect them.'
					/>
				)}
			</p>
			<p>
				{isSwedenFlow ? (
					<FormattedMessage
						id='flow.accounts.description3.sweden'
						defaultMessage="Once you've added all your accounts, you can proceed to the next step."
					/>
				) : (
					<FormattedMessage
						id='flow.accounts.description3'
						defaultMessage='Once all accounts and credit cards have been added, you can proceed to the next step.'
					/>
				)}
			</p>
			<Title>
				<FormattedMessage id='flow.accounts.listTitle' defaultMessage='Your selected banks' />
			</Title>
			<List>
				{/* We show the banks as loading while waiting for both the URL and the selected banks to be available */}
				{(selectedBanksResource.status === 'loading' ||
					urlResource.status === 'loading' ||
					(selectedBanksResource.status === 'done' && !selectedBanksResource.value)) && (
					<ItemBank title='' imageUrl={null} accounts={null} isLoading />
				)}
				{selectedBanksResource.status === 'done' &&
					selectedBanksResource.value?.map((bank) => <ItemBank key={bank.id} {...bank} onClick={onPsd2Login} />)}
			</List>
			{selectedBanksResource.status !== 'loading' && urlResource.status === 'done' && urlResource.value !== null && (
				<ButtonLink
					label={
						<>
							<IconPlus />
							<FormattedMessage id='flow.accounts.buttonAddAccounts' defaultMessage='Add financial institutes' />
						</>
					}
					onClick={onPsd2Login}
				/>
			)}
			{showInputError && (
				<InputError
					errorMessage={
						arePsd2BanksSubmitted ? (
							<FormattedMessage
								id='flow.accounts.addedBanksPreviously'
								defaultMessage='Please re-open the flow by pressing "Add financial institutes" and submit your financial institutes by pressing "Continue" in the flow to continue, or add more if any are missing.'
							/>
						) : (
							<FormattedMessage id='flow.accounts.errorNoAccounts' defaultMessage='Add one or more bank to continue' />
						)
					}
					hasError
				/>
			)}
			{psd2Status instanceof Psd2Error && (
				<InfoBox type='warning'>
					<div>
						<IconWarning />
						<h2>
							<FormattedMessage id='flow.accounts.psd2ErrorTitle' defaultMessage='An error occurred' />
						</h2>
					</div>
					<div>
						<p>
							<Psd2loginError type={psd2Status.type} />
						</p>
						<DefaultErrorSteps isOneTimeAuth={user.isOneTimeAuth} />
					</div>
				</InfoBox>
			)}
			{selectedBanksResource.status === 'errored' && urlResource.status === 'done' && (
				<InfoBox type='warning'>
					<h2>
						<FormattedMessage
							id='flow.accounts.showAccountsError'
							defaultMessage='We cannot show you your already added financial institutions at this time'
						/>
					</h2>
					<div>
						<FormattedMessage
							id='flow.accounts.showAccountsErrorDescription'
							defaultMessage='You can try to continue by adding your financial institutions below if possible'
						/>
					</div>
				</InfoBox>
			)}
			{urlResource.status === 'errored' && selectedBanksResource.status !== 'loading' && (
				<InfoBox type='warning'>
					<div>
						<IconWarning />
						<h2>
							<FormattedMessage
								id='flow.accounts.addAccountsError'
								defaultMessage='We cannot add any financial institutions at this time'
							/>
						</h2>
					</div>
					<div>
						<DefaultErrorSteps />
					</div>
				</InfoBox>
			)}
			{arePsd2BanksSubmitted && (
				<InfoBox type='warning'>
					<div>
						<IconWarning />
						<h2>
							<FormattedMessage
								id='flow.accounts.addedBanksPreviouslyTitle'
								defaultMessage='You have previously added your financial institutes, but did not submit them yet'
							/>
						</h2>
					</div>
					<div>
						<FormattedMessage
							id='flow.accounts.addedBanksPreviously'
							defaultMessage='Please re-open the flow by pressing "Add financial institutes" and submit your financial institutes by pressing "Continue" in the flow to continue, or add more if any are missing.'
						/>
					</div>
				</InfoBox>
			)}
			<ButtonGroup>
				<ButtonSolid
					label={<FormattedMessage id='flow.buttonNext' defaultMessage='Continue' />}
					onClick={() => {
						if (psd2Status !== 'success' && isBankDataReceived === false) return setShowInputError(true)
						return onNext()
					}}
				/>
			</ButtonGroup>
		</Card>
	)
}
